import { useSoftDeletePunctualityReport } from '@src/main/modules/reports/punctuality/application/hook/useDeletePunctualityReport';
import { useDownloadPunctualityReport } from '@src/main/modules/reports/punctuality/application/hook/useDownloadPunctualityReport';
import { PunctualityReport } from '@src/main/modules/reports/shared/domain/report.interface';
import { getTimestamp } from '@src/main/shared/utils/dateUtils';
import { logger } from '@src/main/shared/utils/logger';
import saveAs from 'file-saver';
import { useCallback, useState } from 'react';

export const usePunctualityActions = () => {
  const { mutateAsync: downloadPdfMutation } = useDownloadPunctualityReport();
  const { mutateAsync: deleteReportMutation } = useSoftDeletePunctualityReport();

  const [reportToDelete, setReportToDelete] = useState<PunctualityReport | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const downloadPdf = useCallback(
    async (report: PunctualityReport) => {
      try {
        const blob = await downloadPdfMutation(report.id);
        // eslint-disable-next-line max-len
        const fileName = `punctuality_report_${report.carrierCode}${report.flightNumber}_${report.flightDate}_${report.departureAirport}_${report.arrivalAirport}_${getTimestamp()}.pdf`;
        saveAs(blob, fileName);
      } catch (error) {
        logger.error('Error downloading PDF', error);
      }
    },
    [downloadPdfMutation],
  );

  const handleDeleteReport = useCallback((report: PunctualityReport) => {
    setReportToDelete(report);
    setIsDeleteModalVisible(true);
  }, []);

  const confirmDeleteReport = useCallback(
    async (onAfterDelete?: () => void) => {
      if (!reportToDelete) return;
      try {
        await deleteReportMutation(reportToDelete.id);
        onAfterDelete?.();
      } catch (error) {
        logger.error('Error deleting report', error);
      } finally {
        setReportToDelete(null);
        setIsDeleteModalVisible(false);
      }
    },
    [reportToDelete, deleteReportMutation],
  );

  const rejectDeleteReport = useCallback(() => {
    setReportToDelete(null);
    setIsDeleteModalVisible(false);
  }, []);

  return {
    downloadPdf,
    handleDeleteReport,
    confirmDeleteReport,
    rejectDeleteReport,
    reportToDelete,
    isDeleteModalVisible,
    setIsDeleteModalVisible,
  };
};
